/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { LinearProgress, List, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import { useAtomValue } from 'jotai';
import React from 'react';
import { incidentConfigAtom } from '../config/api';
import { IncidentConfigAlert } from '../config/IncidentConfigAlert';
import { IncidentRow } from '../Ipaws/IPAWSAlertList';
import type { IncidentConfig } from '../types';
import { loadable } from '../utils/loadable';

const IncidentRows = ({ incidents }: { incidents: IncidentConfig[] }) => (
  <List>
    {incidents.map(incident => (
      <IncidentRow key={incident.id} event={incident.name} incidentId={incident.id} />
    ))}
  </List>
);

/**
 * The dashboard's incident page lists, grouped by the page type each incident
 * is configured with. The configuration is edited in S3 between deploys, so
 * a bad upload shows up here, as the reason, rather than as an empty list.
 */
export const IncidentLists = React.memo(() => {
  const config = useAtomValue(loadable(incidentConfigAtom));

  const incidents = config.state === 'hasData' ? config.data : [];
  const activations = incidents.filter(i => i.pageType === 'nrcc-activation');
  const watches = incidents.filter(i => i.pageType === 'watch');
  const historical = incidents.filter(i => i.pageType === 'historical');

  return (
    <>
      {config.state === 'loading' && <LinearProgress aria-label="Loading incident pages" />}
      <IncidentConfigAlert title="Incident pages could not be listed" />
      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center', p: '0 8px' }}>
          <Box sx={{ p: 1 }}>
            <Typography variant="h5">Active Incidents</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">NRCC Activations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <IncidentRows incidents={activations} />
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{watches.length || ''} Other Incidents</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <IncidentRows incidents={watches} />
            </AccordionDetails>
          </Accordion>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center', p: '0 8px' }}>
          <Box sx={{ p: 1 }}>
            <Typography variant="h5">
              <HistoryIcon sx={{ mr: 1 }} /> Historical Incidents
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <IncidentRows incidents={historical} />
        </AccordionDetails>
      </Accordion>
    </>
  );
});
